import React from 'react';
import { useRole, type Role } from '../../context/RoleContext';
import { useUser } from '../../context/UserContext';
import { Icon } from '@tviklax/ui-components';
import {
    DropdownContainer,
    ProfileHeader,
    LargeAvatar,
    ProfileInfo,
    ProfileName,
    ProfileEmail,
    ProfileRole,
    MenuList,
    MenuItem,
    MenuIcon,
    MenuContent,
    MenuTitle,
    MenuSubtitle,
    LogoutButton
} from './ProfileDropdown.styles';

interface ProfileDropdownProps {
    onLogout: () => void;
    onNavigate: (tab: string) => void;
    avatarSrc: string;
}

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({ onLogout, onNavigate, avatarSrc }) => {
    const { role: currentRole, roleLabel } = useRole();
    const { user } = useUser();
    const roles: Array<{ id: Role; label: string; subtitle: string; icon: string }> = [
        { id: 'admin', label: 'Admin', subtitle: 'Business owner', icon: 'Shield' },
        { id: 'ops', label: 'Ops', subtitle: 'Logistics / execution', icon: 'Truck' },
        { id: 'compliance', label: 'Compliance', subtitle: 'Customs & docs', icon: 'ClipboardList' },
        { id: 'finance', label: 'Finance', subtitle: 'Payments & invoices', icon: 'CreditCard' },
        { id: 'sales', label: 'Sales', subtitle: 'Front-office', icon: 'Handshake' },
        { id: 'agent', label: 'Agent', subtitle: 'Tvikla Authorized', icon: 'UserCheck' },
        { id: 'customer', label: 'Customer', subtitle: 'Buyer', icon: 'User' },
        { id: 'supplier', label: 'Supplier', subtitle: 'Seller / Manufacturer', icon: 'Factory' },
    ];
    const displayName = user?.name || 'User';
    const displayEmail = user?.email || 'Not available';
    const displayRole = user?.role || roleLabel || roles.find(r => r.id === currentRole)?.label || 'User';

    return (
        <DropdownContainer
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
        >
            <ProfileHeader>
                <LargeAvatar>
                    <img
                        src={avatarSrc}
                        alt="User Profile"
                    />
                </LargeAvatar>
                <ProfileInfo>
                    <ProfileName>{displayName}</ProfileName>
                    <ProfileEmail>
                        <Icon name="Mail" size={14} />
                        {displayEmail}
                    </ProfileEmail>
                    <ProfileRole>
                        <Icon name="User" size={14} />
                        {displayRole}
                    </ProfileRole>
                </ProfileInfo>
            </ProfileHeader>

            <MenuList>
                <MenuItem onClick={() => onNavigate('profile-settings')}>
                    <MenuIcon>
                        <Icon name="User" size={20} />
                    </MenuIcon>
                    <MenuContent>
                        <MenuTitle>My Profile</MenuTitle>
                        <MenuSubtitle>Profile settings</MenuSubtitle>
                    </MenuContent>
                </MenuItem>

                <MenuItem onClick={() => onNavigate('change-password')}>
                    <MenuIcon>
                        <Icon name="Key" size={20} />
                    </MenuIcon>
                    <MenuContent>
                        <MenuTitle>Change password</MenuTitle>
                        <MenuSubtitle>Password settings</MenuSubtitle>
                    </MenuContent>
                </MenuItem>

                <MenuItem onClick={() => onNavigate('business-settings')}>
                    <MenuIcon>
                        <Icon name="Settings" size={20} />
                    </MenuIcon>
                    <MenuContent>
                        <MenuTitle>Business settings</MenuTitle>
                        <MenuSubtitle>Tvikla settings</MenuSubtitle>
                    </MenuContent>
                </MenuItem>
            </MenuList>

            <LogoutButton onClick={onLogout}>
                Logout
            </LogoutButton>
        </DropdownContainer>
    );
};

export default ProfileDropdown;
